/**
 * Sample data, loaded only when you press the button in Settings.
 *
 * Nothing is ever seeded automatically: a new install starts completely empty.
 * The sample covers roughly five weeks ending today, so the Week and Month
 * views have something realistic to show while you try the app out.
 */

import { addDays, isWeekend, today } from './dates.js';
import { DEFAULT_EXPENSE_CATEGORIES, DEFAULT_WORKSTREAMS, emptyDay, uid } from './model.js';

const WS = DEFAULT_WORKSTREAMS;

const DONE_POOL = [
  ['Reviewed US 3PL service levels against the January baseline', 0],
  ['Call with the Ohio warehouse on pick accuracy and the short-ship backlog', 0],
  ['Drafted the service recovery plan for east coast orders', 0],
  ['Rebuilt the cost to serve model with the new pallet rates', 1],
  ['Checked landed cost assumptions against three recent import entries', 1],
  ['Worked through duty treatment on the two new SKU families', 1],
  ['Compared quotes from the three shortlisted hauliers', 2],
  ['Mapped current network flows and volumes by lane', 2],
  ['Site visit notes written up for the Midlands depot', 2],
  ['Reviewed the S&OP calendar and the planning handover pack', 3],
  ['Sat in on the demand review and noted the gaps in the forecast inputs', 3],
  ['Set up the weekly planning control report', 3],
  ['Expenses and timesheet admin', 4],
  ['Weekly update written and sent', 4],
];

const PLANNED_POOL = [
  ['Finish the service recovery plan and circulate for comment', 0],
  ['Get the updated freight rate card from procurement', 2],
  ['Model two scenarios for the warehouse consolidation', 2],
  ['Agree the planning KPI definitions with the ops team', 3],
  ['Follow up on the missing import entry paperwork', 1],
  ['Book the site visit for the Bridgend stock count', 0],
];

const WAITING_POOL = [
  ['Finance', 'Confirmed freight spend by lane for the last six months'],
  ['Ohio warehouse', 'Pick accuracy data for the last four weeks'],
  ['Procurement', 'Signed-off rate card from the incumbent haulier'],
  ['Client contact', 'Decision on the scope of the planning workstream'],
];

/** Small deterministic pseudo-random generator, so the sample is repeatable. */
function makeRandom(seed) {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) % 2147483648;
    return state / 2147483648;
  };
}

export function buildSampleDays(endDate = today(), weeks = 5) {
  const random = makeRandom(20260101);
  const days = [];
  const start = addDays(endDate, -(weeks * 7 - 1));

  // Waiting-on items are created once and then carried forward, exactly as the
  // app does day to day. `nextWaiting` walks the pool so each raised item is a
  // different one, even after earlier items have been cleared.
  let openItems = [];
  let nextWaiting = 0;

  for (let i = 0; i < weeks * 7; i += 1) {
    const date = addDays(start, i);
    const weekend = isWeekend(date);

    const day = emptyDay(date, 'Bridgend');

    if (weekend) {
      // Weekends are left blank unless something was genuinely worked.
      if (random() > 0.9) {
        day.dayType = 'Non-billable';
        day.location = 'Remote';
        day.done = [{ id: uid(), text: 'Caught up on notes and the week ahead', workstream: WS[4] }];
      } else {
        day.dayType = 'Not working';
        // Carry the open waiting-on items so the count keeps running.
        day.waitingOn = openItems.map((item) => ({ ...item }));
        days.push(day);
        continue;
      }
    } else {
      const roll = random();
      day.dayType = roll > 0.92 ? 'Non-billable' : roll > 0.84 ? 'Half' : 'Full';
      const locationRoll = random();
      day.location = locationRoll > 0.78 ? 'Remote' : locationRoll > 0.7 ? 'Travel' : 'Bridgend';

      const count = day.dayType === 'Half' ? 1 : 2 + Math.floor(random() * 2);
      const picked = new Set();
      for (let n = 0; n < count; n += 1) {
        const index = Math.floor(random() * DONE_POOL.length);
        if (picked.has(index)) continue;
        picked.add(index);
        const [text, ws] = DONE_POOL[index];
        day.done.push({ id: uid(), text, workstream: WS[ws] });
      }

      const plannedCount = 1 + Math.floor(random() * 2);
      for (let n = 0; n < plannedCount; n += 1) {
        const [text, ws] = PLANNED_POOL[Math.floor(random() * PLANNED_POOL.length)];
        if (day.planned.some((p) => p.text === text)) continue;
        day.planned.push({
          id: uid(),
          text,
          workstream: WS[ws],
          // Most planned items get picked up the next day.
          done: random() > 0.45,
          carried: n === 0 && random() > 0.6,
        });
      }

      // Expenses: fuel on travel days, the odd hotel and lunch.
      if (day.location === 'Travel' || day.location === 'Bridgend') {
        const km = 60 + Math.round(random() * 180);
        day.expenses.push({
          id: uid(),
          category: 'Fuel',
          amount: Math.round(km * 0.45 * 100) / 100,
          km,
          note: 'Return journey to site',
          receiptRef: `FUEL-${date.slice(5).replace('-', '')}`,
        });
      }
      if (random() > 0.85) {
        day.expenses.push({
          id: uid(),
          category: 'Hotel',
          amount: 89 + Math.round(random() * 40),
          km: '',
          note: 'Overnight near site',
          receiptRef: `HOT-${date.slice(5).replace('-', '')}`,
        });
      }
      if (random() > 0.7) {
        day.expenses.push({
          id: uid(),
          category: 'Sustenance',
          amount: Math.round((7 + random() * 12) * 100) / 100,
          km: '',
          note: 'Lunch on site',
          receiptRef: '',
        });
      }

      if (random() > 0.82) {
        day.notes = 'Traffic on the M4 added an hour each way. Worth starting earlier next time.';
      }

      // Occasionally raise a new waiting-on item, and occasionally clear one.
      if (random() > 0.82 && nextWaiting < WAITING_POOL.length) {
        const [who, what] = WAITING_POOL[nextWaiting];
        nextWaiting += 1;
        openItems.push({ id: uid(), who, what, sinceDate: date, clearedDate: '' });
      }
      if (random() > 0.9 && openItems.length > 1) {
        const cleared = openItems.shift();
        day.waitingOn.push({ ...cleared, clearedDate: date });
      }
    }

    day.waitingOn = [...openItems.map((item) => ({ ...item })), ...day.waitingOn];
    days.push(day);
  }

  return days;
}

export function sampleSettings() {
  return {
    id: 'settings',
    consultantName: 'Your name',
    companyName: 'Your Company Ltd',
    companyNumber: '12345678',
    clientName: 'Client Ltd',
    clientContact: 'Operations Director',
    defaultLocation: 'Bridgend',
    workstreams: [...DEFAULT_WORKSTREAMS],
    expenseCategories: [...DEFAULT_EXPENSE_CATEGORIES],
  };
}
